'use client'
import { createContext, useContext, useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { getMemberForProperty, saveMemberSession, getMemberSession, clearMemberSession, getUserMembershipProperties, checkMemberDetails } from '@/services/membershipApi';
import { getMemberLoginSession, getCurrentMemberLogin, clearMemberLoginSession } from '@/services/memberLoginApi';
import { getPropertyCode } from '@/utils/propertyManager';

const MembershipContext = createContext();

export const useMembership = () => {
  const context = useContext(MembershipContext);
  if (!context) {
    throw new Error('useMembership must be used within a MembershipProvider');
  }
  return context;
};

export const MembershipProvider = ({ children }) => {
  const { data: session, status } = useSession();
  const [memberData, setMemberData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPropertyMembership, setHasPropertyMembership] = useState(false);
  const [userMembershipProperties, setUserMembershipProperties] = useState([]);
  const [error, setError] = useState(null);
  const [manualLoginData, setManualLoginData] = useState(null);
  const [fallbackMemberData, setFallbackMemberData] = useState(null); // Store member data even if property doesn't match

  // Check membership when session changes or property changes
  useEffect(() => {
    const checkMembership = async () => {
      // While session is loading, preserve existing membership data
      // This prevents clearing memberData on page navigation (e.g., back from membership page)
      if (status === 'loading') {
        console.log('MembershipContext: Session loading, preserving existing data');
        return;
      }
      
      // Check for manual login first
      const currentManualLogin = getCurrentMemberLogin();
      const propertyCode = getPropertyCode();
      
      if (currentManualLogin && propertyCode) {
        const manualSession = getMemberLoginSession(propertyCode);
        if (manualSession && manualSession.email === currentManualLogin.email) {
          setManualLoginData(manualSession.memberData);
          setMemberData(manualSession.memberData);
          setHasPropertyMembership(true);
          setUserMembershipProperties([manualSession.memberData.hotel_id]);
          setIsLoading(false);
          return;
        }
      }
      
      // If no manual login, check OAuth session
      // Only clear data when session is confirmed unauthenticated (not just loading)
      if (!session?.user?.email) {
        // Clear manual login data only when session is confirmed unauthenticated
        setManualLoginData(null);
        setMemberData(null);
        setHasPropertyMembership(false);
        setUserMembershipProperties([]);
        clearMemberSession();
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // First get all properties where user has membership
        const membershipProperties = await getUserMembershipProperties(session.user.email);
              
        const propertyCode = getPropertyCode();
              
        setUserMembershipProperties(membershipProperties);
              
        if (!propertyCode) {
          setHasPropertyMembership(membershipProperties.length > 0);
          setIsLoading(false);
          return;
        }
      
        // Check if current property has membership
        const hasCurrentPropertyMembership = membershipProperties.includes(propertyCode);
        setHasPropertyMembership(hasCurrentPropertyMembership);
              
        // Fetch member data regardless of property match - we need member_code for reservations
        // First check session storage for this property
        const sessionData = getMemberSession(propertyCode);
              
        let memberInfo = null;
              
        if (sessionData && 
            sessionData.email === session.user.email &&
            sessionData.memberData?.hotel_id === propertyCode) {
          memberInfo = sessionData.memberData;
        } else {
          // Fetch from API
          memberInfo = await getMemberForProperty(session.user.email, propertyCode);
        }
      
        if (hasCurrentPropertyMembership && memberInfo) {
          // User has membership for this property
          setMemberData(memberInfo);
          saveMemberSession(session.user.email, memberInfo);
        } else {
          // User doesn't have membership for this property, but we still need their member_code
          setMemberData(null);
                
          if (memberInfo) {
            setFallbackMemberData(memberInfo);
          } else if (membershipProperties.length > 0) {
            // Try to get member info from any property they have membership at
            const anyMemberInfo = await getMemberForProperty(session.user.email, membershipProperties[0]);
            if (anyMemberInfo) {
              setFallbackMemberData(anyMemberInfo);
            }
          } else {
            // No membership at any property - try direct API call to get any member data
            const directMemberResponse = await checkMemberDetails(session.user.email);
            if (directMemberResponse.success && directMemberResponse.data && directMemberResponse.data.length > 0) {
              setFallbackMemberData(directMemberResponse.data[0]);
            }
          }
        }
      } catch (err) {
        console.error('Error checking membership:', err);
        setError(err.message);
        setMemberData(null);
        setHasPropertyMembership(false);
        setUserMembershipProperties([]);
      } finally {
        setIsLoading(false);
      }
    };

    checkMembership();
  }, [session?.user?.email, status]);

  // Function to handle manual login success
  const handleManualLoginSuccess = async (memberData) => {
    setManualLoginData(memberData);
    setIsLoading(true);
    
    // Fetch complete member details with benefits
    const propertyCode = memberData.hotel_id;
    const email = memberData.email;
    
    try {
      // Clear any existing session data to force a fresh fetch
      clearMemberSession(propertyCode);
      
      // Get complete member details with benefits from the details endpoint
      const completeData = await getMemberForProperty(email, propertyCode);
      
      if (completeData) {
        console.log('Fetched complete member data with benefits:', {
          email,
          propertyCode,
          benefitsCount: completeData.benefits?.length || 0
        });
        setMemberData(completeData);
        saveMemberSession(email, completeData);
      } else {
        console.warn('Could not fetch complete member data, using login data');
        setMemberData(memberData); // Fallback to login data
      }
    } catch (err) {
      console.error('Error fetching complete member data:', err);
      setMemberData(memberData); // Fallback to login data
    } finally {
      setIsLoading(false);
    }
    
    setHasPropertyMembership(true);
    setUserMembershipProperties([memberData.hotel_id]);
    setError(null);
    
    // Force a refresh of the membership data
    setTimeout(() => {
      refreshMembership();
    }, 300);
  };

  // Function to handle manual logout
  const handleManualLogout = () => {
    setManualLoginData(null);
    clearMemberLoginSession();
    
    // If there's an OAuth session, refresh membership for that session
    if (session?.user?.email) {
      refreshMembership();
    } else {
      setMemberData(null);
      setHasPropertyMembership(false);
      setUserMembershipProperties([]);
    }
  };

  // Check if user is manually logged in
  const isManuallyLoggedIn = () => {
    return manualLoginData !== null;
  };

  // Get current user email (manual login takes precedence)
  const getCurrentUserEmail = () => {
    return manualLoginData?.email || session?.user?.email;
  };

  // Refresh membership data
  const refreshMembership = async () => {
    const currentEmail = getCurrentUserEmail();
    if (!currentEmail) return;
    
    const propertyCode = getPropertyCode();
    if (!propertyCode) return;

    setIsLoading(true);
    setError(null);

    try {
      const memberInfo = await getMemberForProperty(currentEmail, propertyCode);
      
      if (memberInfo) {
        setMemberData(memberInfo);
        setHasPropertyMembership(true);
        saveMemberSession(currentEmail, memberInfo);
      } else {
        setMemberData(null);
        setHasPropertyMembership(false);
        clearMemberSession(propertyCode);
      }
    } catch (err) {
      console.error('Error refreshing membership:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Get member level info
  const getMemberLevel = () => {
    if (!memberData) return null;
    
    return {
      level: memberData.current_level || 'Member',
      color: memberData.color_card || '#E5E4E2',
      type: memberData.level_type || '',
      totalSpent: parseFloat(memberData.total_spent) || 0,
      totalStays: parseInt(memberData.total_stays) || 0,
      depositBalance: parseFloat(memberData.deposit_balance) || 0
    };
  };

  // Check if has deposit balance
  const hasDeposit = () => {
    if (!memberData) return false;
    const balance = parseFloat(memberData.deposit_balance) || 0;
    return balance > 0;
  };

  // Get deposit balance
  const getDepositBalance = () => {
    if (!memberData) return 0;
    return parseFloat(memberData.deposit_balance) || 0;
  };

  // Check if can pay with deposit
  const canPayWithDeposit = (amount) => {
    const balance = getDepositBalance();
    return balance >= amount;
  };

  // Get member benefits
  const getBenefits = () => {
    if (!memberData || !memberData.benefits) return [];
    return memberData.benefits;
  };

  // Get discount benefits only
  const getDiscountBenefits = () => {
    const benefits = getBenefits();
    return benefits.filter(benefit => 
      benefit.discount && 
      benefit.discount.type && 
      benefit.discount.type !== null
    );
  };

  // Get non-discount benefits
  const getNonDiscountBenefits = () => {
    const benefits = getBenefits();
    return benefits.filter(benefit => 
      !benefit.discount || 
      !benefit.discount.type || 
      benefit.discount.type === null
    );
  };

  // Check if user has membership for any property (for FloatingLoginButton visibility)
  const hasAnyMembership = () => {
    return userMembershipProperties.length > 0;
  };

  const value = {
    memberData,
    fallbackMemberData, // Member data from another property (for member_code)
    isLoading,
    hasPropertyMembership,
    userMembershipProperties,
    hasAnyMembership,
    error,
    refreshMembership,
    getMemberLevel,
    hasDeposit,
    getDepositBalance,
    canPayWithDeposit,
    getBenefits,
    getDiscountBenefits,
    getNonDiscountBenefits,
    handleManualLoginSuccess,
    handleManualLogout,
    isManuallyLoggedIn,
    getCurrentUserEmail,
    manualLoginData
  };

  return (
    <MembershipContext.Provider value={value}>
      {children}
    </MembershipContext.Provider>
  );
};
